// Notice Types — the admin screen for notice_types (Utilities → Table Management). The kinds of additional notice
// a parcel year can carry beside the original Proposed (Supplemental, Corrected, Reverse Appeal …). Built like
// DocFoldersTable; see docs/notice-types-step3-plan.md.
//
// ⛔ KEYS NEVER CHANGE; NAMES DO. A rename shows everywhere at once.
// ⛔ EDITING IS DATABASE-ENFORCED. RLS lets only staff with ntrg_staff.manages_doc_types write; other staff read.
// Every write checks the affected-row count, because a write the policy blocks returns 0 rows and NO error.
// ⛔ DELETE ONLY WHAT NO NOTICE USES (step 6). parcel_notices.type_key links here ON DELETE RESTRICT, so the database
// refuses deleting a type in use; the screen checks first and says "Used by N notices — retire it instead".
// ⛔ WHO MAY EDIT IS ASKED OF THE DATABASE, via the same manages_doc_types() function the write policies call.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

const bySort = (a, b) => (a.sort - b.sort) || a.name.localeCompare(b.name);
const slug = (name) => String(name || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60) || 'item';

export default function NoticeTypesTable({ c, s }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [rights, setRights] = useState({ checked: false, error: null });   // the edit-rights answer, or why there is none
  const [editing, setEditing] = useState(null);    // { key, name } — an inline rename in progress
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);            // { tone:'error'|'info', text }

  // RLS returns an empty list with no error to someone it doesn't let read, so 0 rows is reported, not shown as empty.
  const load = useCallback(async () => {
    const { data, error } = await supabase.from('notice_types').select('*');
    if (error) setMsg({ tone: 'error', text: `Could not load notice types: ${error.message}` });
    else if (!data?.length) setMsg({ tone: 'error', text: 'No notice types came back — a permissions problem, not an empty list.' });
    setTypes([...(data || [])].sort(bySort));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    supabase.rpc('manages_doc_types').then(({ data, error }) => {
      setCanEdit(!error && data === true);
      setRights({ checked: true, error: error ? error.message : null });
    }, (e) => setRights({ checked: true, error: e.message || 'the check did not complete' }));
  }, [load]);

  const friendly = (e) => e.code === '23505' ? 'that name is already in use' : e.code === '23514' ? 'a name can’t be empty' : e.message;
  const fail = (what, error) => { setMsg({ tone: 'error', text: `${what} — not saved: ${error ? friendly(error) : '0 rows changed (no permission?)'}` }); return false; };

  // Every update: affected-row check, then reload.
  async function update(key, patch, what) {
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.from('notice_types').update({ ...patch, updated_at: new Date().toISOString() }).eq('key', key).select('key');
    setBusy(false);
    if (error || !data?.length) return fail(what, error);
    await load();
    return true;
  }

  // Reorder by swapping sort with the neighbour.
  async function move(idx, dir) {
    const a = types[idx], b = types[idx + dir];
    if (!a || !b) return;
    const aSort = b.sort === a.sort ? a.sort + dir : b.sort;
    const bSort = a.sort;
    if (!(await update(a.key, { sort: aSort }, `Reorder "${a.name}"`))) return;
    await update(b.key, { sort: bSort }, `Reorder "${b.name}"`);
  }

  async function saveRename() {
    const name = String(editing.name || '').trim();
    if (!name) { setMsg({ tone: 'error', text: 'A name can’t be empty.' }); return; }
    if (await update(editing.key, { name }, `Rename to "${name}"`)) setEditing(null);
  }

  // Checked first (count of notices of this type), and the RESTRICT link refuses it anyway (23503) if one slipped in.
  async function remove(row) {
    setMsg(null);
    const inUse = (n) => setMsg({ tone: 'error', text: `Used by ${n == null ? '' : `${n} `}notice${n === 1 ? '' : 's'} — retire it instead.` });
    const { count, error: cErr } = await supabase.from('parcel_notices').select('id', { count: 'exact', head: true }).eq('type_key', row.key);
    if (cErr) { setMsg({ tone: 'error', text: `Could not check "${row.name}" — not deleted: ${cErr.message}` }); return; }
    if (count > 0) { inUse(count); return; }
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    setBusy(true);
    const { data, error } = await supabase.from('notice_types').delete().eq('key', row.key).select('key');
    setBusy(false);
    if (error?.code === '23503') { inUse(null); return; }
    if (error || !data?.length) { fail(`Delete "${row.name}"`, error); return; }
    await load();
  }

  async function retire(row) {
    const next = !row.active;
    if (!next && !window.confirm(`Retire "${row.name}"? It disappears from the Notice Type window. Notices already of this type keep it.`)) return;
    await update(row.key, { active: next }, `${next ? 'Restore' : 'Retire'} "${row.name}"`);
  }

  async function saveAdd() {
    const name = String(draft || '').trim();
    if (!name) { setMsg({ tone: 'error', text: 'Give it a name.' }); return; }
    const taken = new Set(types.map(t => t.key));
    let key = slug(name), i = 2;
    while (taken.has(key)) key = `${slug(name)}_${i++}`;
    setBusy(true); setMsg(null);
    const { data, error } = await supabase.from('notice_types')
      .insert({ key, name, sort: Math.max(0, ...types.map(t => t.sort)) + 10 }).select('key');
    setBusy(false);
    if (error || !data?.length) return fail(`Add "${name}"`, error);
    setAdding(false); setDraft('');
    await load();
  }

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;

  const small = { ...s.btnGhost, padding: '3px 8px', fontSize: '11px' };
  const input = { padding: '5px 8px', fontSize: '12px', border: `1px solid ${c.inputBorder}`, borderRadius: '6px', background: c.input, color: c.text };

  return (
    <div style={s.card}>
      <div style={s.cardH}>
        <div>
          <div style={s.cardT}>Notice Types</div>
          <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
            The kinds of notice a parcel year can carry beside the original Proposed notice. Retired types leave the Notice Type window; notices already of that type keep it.
            {!rights.checked ? ' Checking your edit rights…'
              : rights.error ? ` Your edit rights could not be checked (${rights.error}) — read-only until you reload the page.`
              : canEdit ? '' : ' Read-only — only staff with document-type admin rights can edit.'}
          </div>
        </div>
      </div>
      {msg && <div style={{ padding: '10px 18px', fontSize: '12px', fontWeight: '600', color: msg.tone === 'error' ? 'var(--red)' : '#34d399', borderBottom: `1px solid ${c.border}` }}>{msg.text}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={s.th}>Notice type</th><th style={s.th} /></tr></thead>
        <tbody>
          {types.map((t, i) => (
            <tr key={t.key} style={{ opacity: t.active ? 1 : 0.55 }}>
              <td style={s.td}>
                {editing && editing.key === t.key ? (
                  <span style={{ display: 'inline-flex', gap: '6px' }}>
                    <input autoFocus value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}
                      onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditing(null); }} style={input} />
                    <button style={small} disabled={busy} onClick={saveRename}>Save</button>
                    <button style={small} onClick={() => setEditing(null)}>✕</button>
                  </span>
                ) : (
                  <span>{t.name}{canEdit && <button style={{ ...small, marginLeft: '8px' }} onClick={() => setEditing({ key: t.key, name: t.name })}>✎</button>}</span>
                )}
                {!t.active && <span style={{ ...s.badge('248,113,113'), marginLeft: '6px' }}>Retired</span>}
              </td>
              <td style={{ ...s.td, whiteSpace: 'nowrap', textAlign: 'right' }}>{canEdit && <>
                <button style={small} disabled={busy || i === 0} onClick={() => move(i, -1)}>↑</button>
                <button style={{ ...small, marginLeft: '4px' }} disabled={busy || i === types.length - 1} onClick={() => move(i, 1)}>↓</button>
                <button style={{ ...small, marginLeft: '6px' }} disabled={busy} onClick={() => retire(t)}>{t.active ? 'Retire' : 'Restore'}</button>
                <button style={{ ...small, marginLeft: '4px', color: '#f87171' }} disabled={busy} onClick={() => remove(t)}>Delete</button>
              </>}</td>
            </tr>
          ))}
          {canEdit && (adding ? (
            <tr><td colSpan="2" style={s.td}>
              <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                <input autoFocus placeholder="Notice type name" value={draft} onChange={e => setDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveAdd(); if (e.key === 'Escape') { setAdding(false); setDraft(''); } }} style={input} />
                <button style={s.btn} disabled={busy} onClick={saveAdd}>Add notice type</button>
                <button style={small} onClick={() => { setAdding(false); setDraft(''); }}>Cancel</button>
              </span>
            </td></tr>
          ) : (
            <tr><td colSpan="2" style={{ ...s.td, borderBottom: 'none' }}><button style={small} onClick={() => { setAdding(true); setDraft(''); }}>+ Add Notice Type</button></td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
